import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { SearchOutlined, PeopleAltOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useShellTranslation } from './useShellTranslation';
import { useQuery } from '@tanstack/react-query';
import { shellTokens } from './shellTokens';
import { useShell } from './ShellContext';
import { useAuth } from '../../contexts/AuthContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { brandDirectoryApi } from '../../services/brandDirectoryApi';
import { NavModel } from './useNavModel';
import { useOverlayFocus } from './useOverlayFocus';
import { shellViewAsShortcut } from './shellShortcut';
import {
  buildViewAsTargets,
  capViewAsRows,
  rankViewAsTargets,
  viewAsAccountKey,
  viewAsGroup,
  ViewAsGroup,
} from './viewAsTargets';

const { color, radius, shadow, z } = shellTokens;

/** Rows rendered in the View-as list before the "type to search" hint kicks in. */
const MAX_VIEW_AS_ROWS = 50;

interface PaletteRow {
  key: string;
  label: string;
  path: string;
  badge?: number;
  /** Heading this row sits under (View-as mode only). */
  group?: ViewAsGroup;
  /** True for the account currently being impersonated (View-as mode only). */
  current?: boolean;
  onActivate: () => void;
}

const KeyHint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="span"
    sx={{
      px: '5px',
      py: '2px',
      border: `1px solid ${color.line}`,
      borderRadius: radius.keyHint,
      fontSize: '11px',
      color: color.faint,
      background: color.surface,
    }}
  >
    {children}
  </Box>
);

interface CommandPaletteProps {
  nav: NavModel;
  canViewAs: boolean;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ nav, canViewAs }) => {
  const { t } = useShellTranslation();
  const navigate = useNavigate();
  const { overlay, closeOverlay, setOverlay } = useShell();
  const { userProfile } = useAuth();
  const {
    startImpersonationWithOrg,
    startImpersonationWithBrand,
    impersonatedOrg,
    impersonatedBrand,
  } = useImpersonation();
  const [query, setQuery] = useState('');
  // The row the admin moved the highlight to; null means they have not, and the
  // highlight is on the top row, whichever that becomes. A chosen row is followed
  // by key, so rows arriving late cannot move it onto a different account.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const open = overlay?.kind === 'palette';
  const { containerRef, onKeyDown: onFocusKeyDown } = useOverlayFocus<HTMLDivElement>(open, {
    initialFocusRef: inputRef,
  });
  const mode = open ? overlay.mode : 'nav';

  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'super_admin';

  const { data: organizationDirectory, isLoading: orgsLoading } = useQuery({
    queryKey: ['organization-directory-impersonation'],
    // `/admin/organizations` paginates and ignores its search argument, so a
    // capped first page was all the palette could ever filter — accounts past
    // it were unreachable, and their claimed brands are (correctly) kept out of
    // the brand-only list too. The directory endpoint reads the whole
    // organizations collection in one pass.
    queryFn: () => brandDirectoryApi.listOrganizations(),
    // Only load the impersonation roster when an admin actually opens that mode.
    enabled: Boolean(isAdmin) && open && mode === 'as',
    staleTime: 60 * 1000,
  });

  const { data: brandDirectory, isLoading: brandsLoading } = useQuery({
    queryKey: ['brand-directory-impersonation'],
    // The directory carries claimedBy.organizationId — the stable claim link.
    queryFn: () => brandDirectoryApi.list(),
    enabled: Boolean(isAdmin) && open && mode === 'as',
    staleTime: 60 * 1000,
  });

  const rosterLoading = Boolean(isAdmin) && open && mode === 'as' && (orgsLoading || brandsLoading);

  const viewAsTargets = useMemo(
    () =>
      // A brand can only be told apart from an account once the organizations are
      // in, so brands wait for them and only ever append below. A failed
      // organization read still lets them through on the brand path.
      buildViewAsTargets(organizationDirectory?.organizations, orgsLoading ? undefined : brandDirectory?.brands, {
        store: t('shell.viewAs.type.store'),
        vendor: t('shell.viewAs.type.vendor'),
        brand: t('shell.viewAs.type.brand'),
        pending: t('shell.viewAs.pending'),
      }),
    [organizationDirectory, orgsLoading, brandDirectory, t],
  );

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelectedKey(null);
    // Focus after the panel paints so the caret lands in the input.
    const timer = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [open, mode]);

  const { rows, total } = useMemo<{ rows: PaletteRow[]; total: number }>(() => {
    const needle = query.trim().toLowerCase();

    if (mode === 'as') {
      const ranked = rankViewAsTargets(viewAsTargets, needle);
      const visible = capViewAsRows(ranked, MAX_VIEW_AS_ROWS, selectedKey);
      return {
        total: ranked.length,
        rows: visible.map((target) => ({
          // The account, not the row: a search can swap the name an account is
          // listed under when the brand directory answers.
          key: viewAsAccountKey(target),
          label: target.name,
          path: target.meta,
          group: viewAsGroup(target),
          current:
            target.kind === 'brand'
              ? impersonatedBrand?.id === target.id
              : !impersonatedBrand && impersonatedOrg?.id === target.id,
          onActivate: () => {
            closeOverlay();
            // The impersonation context cancels and clears the account-scoped
            // cache as part of the switch.
            if (target.kind === 'brand') {
              startImpersonationWithBrand({ id: target.id, name: target.name });
            } else {
              startImpersonationWithOrg({
                id: target.id,
                name: target.impersonationName || target.name,
                type: target.type,
                status: target.status,
              });
            }
            navigate('/app/dashboard');
          },
        })),
      };
    }

    const roleLabel = t(`shell.role.${nav.role}`);
    const navRows = nav.entries
      .map((entry) => {
        const label = t(`shell.item.${entry.leaf.labelKey}`);
        const group = entry.groupLabelKey ? t(`shell.group.${entry.groupLabelKey}`) : null;
        return {
          key: entry.leaf.id,
          label,
          path: group ? `${roleLabel} · ${group}` : roleLabel,
          badge: nav.badgeFor(entry.leaf),
          onActivate: () => {
            closeOverlay();
            navigate(entry.leaf.path);
          },
        };
      })
      .filter(
        (row) =>
          !needle ||
          row.label.toLowerCase().includes(needle) ||
          row.path.toLowerCase().includes(needle),
      );
    return { rows: navRows, total: navRows.length };
  }, [
    mode,
    query,
    viewAsTargets,
    selectedKey,
    impersonatedOrg,
    impersonatedBrand,
    nav,
    t,
    navigate,
    closeOverlay,
    startImpersonationWithOrg,
    startImpersonationWithBrand,
  ]);

  // -1 when the chosen row has gone: nothing is highlighted, so Enter cannot open
  // a different account than the one the admin picked.
  const selected = selectedKey === null ? 0 : rows.findIndex((row) => row.key === selectedKey);

  // Arrow keys can walk the selection past the 52vh results viewport; without
  // this you can press Enter on a row you cannot see.
  useEffect(() => {
    if (!open) return;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-index="${selected}"]`);
    row?.scrollIntoView({ block: 'nearest' });
    // Keyed on the query rather than the row count: rows appended below must
    // not scroll the list back to the highlight.
  }, [selected, open, query]);

  if (!open) return null;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Arrow keys drive the result selection here, not roving focus, so the
    // shared handler only needs to own the Tab trap.
    if (event.key === 'Tab') {
      onFocusKeyDown(event);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedKey(rows[Math.min(selected + 1, rows.length - 1)]?.key ?? null);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedKey(rows[Math.max(selected - 1, 0)]?.key ?? null);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      rows[selected]?.onActivate();
    }
  };

  return (
    <Box
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closeOverlay();
      }}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: z.palette,
        background: 'rgba(15,23,42,.32)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        pt: '12vh',
        px: '16px',
      }}
    >
      <Box
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-label={t(mode === 'as' ? 'shell.viewAs.title' : 'shell.palette.title')}
        onKeyDown={handleKeyDown}
        sx={{
          width: 600,
          maxWidth: '92vw',
          background: color.white,
          borderRadius: radius.panel,
          boxShadow: shadow.palette,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            p: '14px 16px',
            borderBottom: `1px solid ${color.lineSubtle}`,
          }}
        >
          <SearchOutlined sx={{ fontSize: 17, color: color.faint, flexShrink: 0 }} />
          {mode === 'as' && (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                px: '8px',
                py: '3px',
                borderRadius: radius.menuRow,
                background: color.impersonation,
                color: color.white,
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              {t('shell.viewAs.chip')}
            </Box>
          )}
          <Box
            component="input"
            ref={inputRef}
            value={query}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setQuery(event.target.value);
              setSelectedKey(null);
            }}
            placeholder={t(
              mode === 'as' ? 'shell.viewAs.placeholder' : 'shell.palette.placeholder',
            )}
            role="combobox"
            aria-expanded
            aria-controls="shell-palette-results"
            aria-activedescendant={rows.length && selected >= 0 ? `shell-palette-row-${selected}` : undefined}
            sx={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              outline: 'none',
              fontSize: '15px',
              color: color.ink,
              '&::placeholder': { color: color.faint },
            }}
          />
          <KeyHint>esc</KeyHint>
        </Box>

        <Box
          ref={listRef}
          id="shell-palette-results"
          role="listbox"
          aria-label={t(mode === 'as' ? 'shell.viewAs.title' : 'shell.palette.title')}
          sx={{ maxHeight: '52vh', overflowY: 'auto', p: '6px' }}
        >
          {rows.length === 0 && (
            <Typography sx={{ p: '18px 10px', fontSize: '13px', color: color.faint }}>
              {mode === 'as' && rosterLoading
                ? t('shell.viewAs.loading')
                : t(mode === 'as' ? 'shell.viewAs.empty' : 'shell.palette.empty')}
            </Typography>
          )}
          {rows.map((row, index) => (
            <React.Fragment key={row.key}>
              {row.group && row.group !== rows[index - 1]?.group && (
                <Typography
                  variant="overline"
                  component="div"
                  role="presentation"
                  sx={{ p: '8px 10px 2px', color: color.faint }}
                >
                  {t(row.group === 'accounts' ? 'shell.viewAs.groupAccounts' : 'shell.viewAs.groupBrands')}
                </Typography>
              )}
              <Box
                id={`shell-palette-row-${index}`}
                role="option"
                aria-selected={index === selected}
                data-index={index}
                tabIndex={-1}
                onMouseEnter={() => setSelectedKey(row.key)}
                onClick={row.onActivate}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  p: '9px 10px',
                  borderRadius: radius.navRow,
                  cursor: 'pointer',
                  background: index === selected ? color.lineSubtle : 'transparent',
                }}
              >
                <Box sx={{ display: 'flex', color: color.faint, '& svg': { fontSize: 16 } }}>
                  {mode === 'as' ? <PeopleAltOutlined /> : <SearchOutlined />}
                </Box>
                <Typography
                  noWrap
                  sx={{ fontSize: '13.5px', fontWeight: 600, color: color.ink, flexShrink: 0 }}
                >
                  {row.label}
                </Typography>
                <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: '12px', color: color.faint }}>
                  {row.path}
                </Typography>
                {row.current ? (
                  <Box
                    component="span"
                    sx={{
                      flexShrink: 0,
                      px: '6px',
                      py: '1px',
                      border: `1px solid ${color.impersonation}`,
                      borderRadius: radius.keyHint,
                      fontSize: '10.5px',
                      fontWeight: 700,
                      color: color.impersonation,
                    }}
                  >
                    {t('shell.viewAs.current')}
                  </Box>
                ) : null}
                {row.badge ? (
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 700,
                      color: color.accentDeep,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {row.badge}
                  </Typography>
                ) : null}
              </Box>
            </React.Fragment>
          ))}
          {mode === 'as' && brandsLoading && rows.length > 0 && (
            <Typography sx={{ p: '10px', fontSize: '12px', color: color.faint, textAlign: 'center' }}>
              {t('shell.viewAs.loadingBrands')}
            </Typography>
          )}
          {mode === 'as' && total > rows.length && (
            <Typography sx={{ p: '10px', fontSize: '12px', color: color.faint, textAlign: 'center' }}>
              {t('shell.viewAs.truncated', { shown: rows.length, total })}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            p: '9px 14px',
            background: color.surface,
            borderTop: `1px solid ${color.lineSubtle}`,
            fontSize: '11.5px',
            color: color.faint,
          }}
        >
          <span>{t('shell.palette.hintNavigate')}</span>
          <span>{t(mode === 'as' ? 'shell.palette.hintViewAs' : 'shell.palette.hintOpen')}</span>
          {mode === 'nav' && canViewAs && (
            <Box
              component="button"
              type="button"
              onClick={() => setOverlay({ kind: 'palette', mode: 'as' })}
              sx={{
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                p: 0,
                font: 'inherit',
                color: 'inherit',
              }}
            >
              {t('shell.palette.hintSwitchToViewAs', { shortcut: shellViewAsShortcut() })}
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          <span>
            {mode === 'as'
              ? t('shell.viewAs.count', { count: total })
              : t('shell.palette.count', { count: total })}
          </span>
        </Box>
      </Box>
    </Box>
  );
};
